/**
 * Compress / downscale an image File for upload (iOS WebView + large originals).
 * Keeps JPEG under the shared 5MB client cap when possible.
 */
export async function prepareImageFileForUpload(
  file: File,
  options?: { maxLongEdge?: number; quality?: number; maxBytes?: number }
): Promise<File> {
  const maxLongEdge = options?.maxLongEdge ?? 2048;
  const quality = options?.quality ?? 0.82;
  const maxBytes = options?.maxBytes ?? 5 * 1024 * 1024;

  if (!file.type.startsWith('image/') || file.type === 'image/gif') {
    return file;
  }

  // Already small enough and not a huge HEIC/PNG original — still re-encode HEIC/WebP to JPEG.
  const needsEncode =
    file.type === 'image/heic' ||
    file.type === 'image/heif' ||
    file.type === 'image/webp' ||
    file.type === 'image/png' ||
    file.size > maxBytes * 0.85 ||
    !file.type.includes('jpeg');

  if (!needsEncode && file.size <= maxBytes) {
    return file;
  }

  const bitmap = await createImageBitmap(file);
  try {
    const longEdge = Math.max(bitmap.width, bitmap.height);
    const scale = longEdge > maxLongEdge ? maxLongEdge / longEdge : 1;
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, width, height);

    let q = quality;
    let blob: Blob | null = null;
    for (let attempt = 0; attempt < 4; attempt++) {
      const qualityThisPass = q;
      blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob((b) => resolve(b), 'image/jpeg', qualityThisPass)
      );
      if (!blob) break;
      if (blob.size <= maxBytes) break;
      q = Math.max(0.55, q - 0.12);
    }
    if (!blob) return file;
    const name = file.name.replace(/\.\w+$/, '') || 'photo';
    return new File([blob], `${name}.jpg`, { type: 'image/jpeg', lastModified: Date.now() });
  } finally {
    bitmap.close();
  }
}
